/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import {
  AlertTriangleIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  BoxIcon,
  CheckIcon,
  HouseIcon,
  LoaderCircleIcon,
  PencilIcon,
  RotateCwIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'

import catalogSource from '../../data/building/catalog.v1.json'
import blueprintSource from '../../data/building/example-canal-house.blueprint.json'
import wharfShopSource from '../../data/building/wharf-shop.blueprint.json'
import keeperTowerSource from '../../data/building/keeper-tower.blueprint.json'
import { loadCatalog } from './catalog'
import { parseReadyBlueprint } from './blueprint'
import { createLocalBuildAdapter, createPlacementCommand, createPlacementDeleteCommand, createPlacementEditCommand, createPlacementPreviewCommand } from './commands'
import { placementAabb, solvePlacement } from './grid'
import { buildReducer, createBuildState } from './state'
import { COLORS, RADII } from '../client/theme'

const PLOT = { min: [-12, 0, -12], max: [12, 18, 12] }
const CATEGORY_LABELS = {
  ready: 'ГОТОВЫЕ ДОМА',
  foundation: 'ФУНДАМЕНТ',
  floor: 'ПОЛ',
  wall: 'СТЕНЫ',
  window: 'ОКНА',
  door: 'ДВЕРИ',
  stairs: 'ЛЕСТНИЦЫ',
  roof: 'КРЫША',
  furniture: 'МЕБЕЛЬ',
  lighting: 'СВЕТ',
}
const REASON_LABELS = {
  OUTSIDE_PLOT: 'ВНЕ УЧАСТКА',
  COLLISION: 'ПЕРЕСЕЧЕНИЕ',
  NO_SUPPORT: 'НЕТ ОПОРЫ',
}
const COMMAND_ERROR_LABELS = {
  REVISION_CONFLICT: 'МИР ИЗМЕНИЛСЯ · ПОВТОРИТЕ ДЕЙСТВИЕ',
  OBJECT_VERSION_CONFLICT: 'ОБЪЕКТ УЖЕ ИЗМЕНИЛСЯ · ВЫБЕРИТЕ ЕГО СНОВА',
  OBJECT_NOT_FOUND: 'ОБЪЕКТ БОЛЬШЕ НЕ СУЩЕСТВУЕТ',
  OBJECT_PROVENANCE_INVALID: 'СИСТЕМНЫЙ ИЛИ НЕИЗВЕСТНЫЙ ОБЪЕКТ НЕЛЬЗЯ РЕДАКТИРОВАТЬ',
  LAND_PERMISSION_DENIED: 'НЕТ АКТИВНОГО ПРАВА НА ЭТО ДЕЙСТВИЕ',
  PLOT_VERSION_CONFLICT: 'ВЛАДЕНИЕ ИЛИ ПРАВА УЧАСТКА ИЗМЕНИЛИСЬ',
  RATE_LIMITED: 'СЛИШКОМ МНОГО BUILD-КОМАНД · ПОДОЖДИТЕ',
  UNDO_TARGET_NOT_FOUND: 'ОБЪЕКТ УЖЕ НЕЛЬЗЯ ОТМЕНИТЬ',
  UNDO_UNAVAILABLE: 'ДЛЯ ЭТОГО ОБЪЕКТА НЕТ КОМАНДЫ ОТМЕНЫ',
  UNSUPPORTED_COMMAND: 'КОМАНДА НЕ ПОДДЕРЖИВАЕТСЯ',
  UNSUPPORTED_PROTOCOL: 'ВЕРСИЯ BUILD MODE НЕ ПОДДЕРЖИВАЕТСЯ',
  INVALID_COMMAND: 'НЕВЕРНАЯ КОМАНДА СТРОИТЕЛЬСТВА',
  INVALID_PLACEMENT: 'ОБЪЕКТ НЕ ПРОШЁЛ ПРОВЕРКУ СЕРВЕРА',
  OFF_GRID: 'ОБЪЕКТ ДОЛЖЕН СТОЯТЬ НА СЕТКЕ 0,5 М',
  OUTSIDE_PLOT: 'ОБЪЕКТ ВЫХОДИТ ЗА ГРАНИЦЫ УЧАСТКА',
  COLLISION: 'МЕСТО ЗАНЯТО ДРУГИМ ОБЪЕКТОМ',
  NO_SUPPORT: 'ОБЪЕКТУ НУЖНА ОПОРА',
  PLACEMENT_LIMIT: 'ДОСТИГНУТ ЛИМИТ ОБЪЕКТОВ ДОМА',
  AUTHORITY_UNAVAILABLE: 'СЕРВЕР СТРОИТЕЛЬСТВА НЕДОСТУПЕН',
  AUTHORITY_TIMEOUT: 'СЕРВЕР СТРОИТЕЛЬСТВА НЕ ОТВЕТИЛ',
  STORAGE_ERROR: 'СЕРВЕР НЕ ПОДТВЕРДИЛ ЗАПИСЬ ДОМА НА ДИСК',
  BUILD_FORBIDDEN: 'СТРОИТЕЛЬСТВО ДОСТУПНО ТОЛЬКО СТРОИТЕЛЮ ИЛИ АДМИНИСТРАТОРУ',
  PLOT_CAPACITY: 'ВСЕ УЧАСТКИ РАЙОНА ЗАНЯТЫ — ЖДИ РАСШИРЕНИЯ',
  COMMAND_FAILED: 'НЕ УДАЛОСЬ СВЯЗАТЬСЯ С BUILD-СЕРВИСОМ',
}
const INTERACTIVE_SELECTOR = 'input, textarea, select, button, [contenteditable="true"], [role="textbox"]'

const itemForPlacement = (placement, catalog) => placement.preview || catalog?.moduleById[placement.moduleId]
const isInteractiveTarget = target => Boolean(target?.closest?.(INTERACTIVE_SELECTOR))

function plotStyle(aabb) {
  const width = PLOT.max[0] - PLOT.min[0]
  const depth = PLOT.max[2] - PLOT.min[2]
  return {
    left: `${((aabb.min[0] - PLOT.min[0]) / width) * 100}%`,
    top: `${((aabb.min[2] - PLOT.min[2]) / depth) * 100}%`,
    width: `${((aabb.max[0] - aabb.min[0]) / width) * 100}%`,
    height: `${((aabb.max[2] - aabb.min[2]) / depth) * 100}%`,
  }
}

export function BuildMode({ world, open, onClose, adapter: suppliedAdapter, canBuild = false }) {
  const [state, dispatch] = useReducer(buildReducer, undefined, createBuildState)
  const adapterRef = useRef(null)
  const commandInFlightRef = useRef(false)
  if (!adapterRef.current) adapterRef.current = suppliedAdapter || createLocalBuildAdapter()
  const adapter = adapterRef.current

  useEffect(() => {
    let active = true
    loadCatalog(Promise.resolve(catalogSource))
      .then(catalog => {
        const readyBlueprints = [blueprintSource, wharfShopSource, keeperTowerSource].map(source =>
          parseReadyBlueprint(source, catalog)
        )
        if (active) dispatch({ type: 'LOAD_SUCCESS', catalog, readyBlueprints })
      })
      .catch(error => active && dispatch({ type: 'LOAD_ERROR', error: error.message }))
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    dispatch({ type: 'SET_SNAPSHOT', snapshot: adapter.getSnapshot() })
    return adapter.subscribe(snapshot => dispatch({ type: 'SET_SNAPSHOT', snapshot }))
  }, [adapter])

  const categories = useMemo(
    () => (state.catalog ? ['ready', ...state.catalog.categories] : ['ready']),
    [state.catalog]
  )
  const items = useMemo(() => {
    if (!state.catalog) return []
    if (state.category === 'ready') return state.readyBlueprints
    return state.catalog.modules.filter(module => module.category === state.category)
  }, [state.catalog, state.category, state.readyBlueprints])
  const selected = useMemo(() => {
    const ready = state.readyBlueprints.find(item => item.id === state.selectedId)
    if (ready) return ready
    return state.catalog?.moduleById[state.selectedId] || null
  }, [state.catalog, state.readyBlueprints, state.selectedId])
  const editing = useMemo(() => state.placements.find(item => item.id === state.editingId) || null, [state.editingId, state.placements])
  const activeItem = editing ? itemForPlacement(editing, state.catalog) : selected
  const occupied = useMemo(() => [...state.placements.filter(item => item.id !== state.editingId), ...state.pending], [state.editingId, state.placements, state.pending])
  const solution = useMemo(() => {
    if (!activeItem || !state.catalog) return null
    return solvePlacement({
      module: activeItem,
      cursor: state.cursor,
      rotationDeg: state.rotationDeg,
      grid: state.catalog.grid,
      plot: PLOT,
      placements: occupied,
      moduleLookup: id => state.catalog.moduleById[id],
      snapTypes: state.catalog.snapTypes,
    })
  }, [activeItem, state.catalog, state.cursor, state.rotationDeg, occupied])

  const authorityOnline = !adapter.persistent || state.authorityOnline
  const busy = state.pending.length > 0 || state.undoPending || !authorityOnline
  const canPlace = canBuild && state.capabilities.includes('build.place')
  const canEdit = canBuild && state.capabilities.includes('build.edit')
  const canDelete = canBuild && state.capabilities.includes('build.delete')
  const move = useCallback(
    (x, z) => {
      if (!state.catalog) return
      dispatch({ type: 'MOVE_CURSOR', delta: [x * state.catalog.grid.horizontalStepM, 0, z * state.catalog.grid.horizontalStepM] })
    },
    [state.catalog]
  )
  const rotate = useCallback(() => {
    if (state.catalog) dispatch({ type: 'ROTATE', step: state.catalog.grid.rotationStepDeg })
  }, [state.catalog])

  const commit = useCallback(async () => {
    const allowed = editing ? canEdit : canPlace
    if (!allowed || !activeItem || !solution?.valid || busy || commandInFlightRef.current || !state.catalog || !state.plotId || !state.plotVersion) return
    commandInFlightRef.current = true
    dispatch({ type: 'MUTATION_STARTED' })
    let command = null
    try {
      const preview = createPlacementPreviewCommand({
        baseRevision: state.revision, plotId: state.plotId, plotVersion: state.plotVersion,
        catalogId: state.catalog.catalogId, item: editing ? null : activeItem, target: editing,
        transform: solution.transform,
      })
      const checked = await adapter.submit(preview)
      if (checked.status !== 'preview' || !checked.preview?.valid) {
        dispatch({ type: 'COMMAND_REJECTED', commandId: preview.commandId, code: checked.preview?.code || checked.code || 'INVALID_PLACEMENT', snapshot: checked.snapshot })
        return
      }
      command = editing
        ? createPlacementEditCommand({
          baseRevision: state.revision, plotId: state.plotId, plotVersion: state.plotVersion,
          targetPlacementId: editing.id, objectVersion: editing.version, transform: solution.transform,
        })
        : createPlacementCommand({
          baseRevision: state.revision, plotId: state.plotId, plotVersion: state.plotVersion,
          catalogId: state.catalog.catalogId, item: activeItem, transform: solution.transform,
        })
      if (!editing) dispatch({
        type: 'OPTIMISTIC_PLACE',
        placement: {
          id: `pending:${command.commandId}`, sourceCommandId: command.commandId,
          moduleId: command.payload.moduleId, blueprintId: command.payload.blueprintId,
          transform: command.payload.transform, preview: command.payload.preview, pending: true,
        },
      })
      const result = await adapter.submit(command)
      if (result.status === 'accepted') {
        dispatch({ type: 'COMMAND_ACCEPTED', commandId: command.commandId, snapshot: result.snapshot })
        world?.ui?.notify?.({ kind: 'success', title: editing ? 'ОБЪЕКТ ОБНОВЛЁН' : 'ОБЪЕКТ УСТАНОВЛЕН', message: adapter.persistent ? 'Authoritative Build State подтверждён сервером.' : 'Локальный preview подтверждён.', dedupeKey: `build:${command.commandId}` })
      } else {
        dispatch({ type: 'COMMAND_REJECTED', commandId: command.commandId, code: result.code, snapshot: result.snapshot })
        world?.ui?.notify?.({ kind: 'error', title: 'BUILD-КОМАНДА ОТКЛОНЕНА', message: COMMAND_ERROR_LABELS[result.code] || result.code, dedupeKey: `build-error:${command.commandId}` })
      }
    } catch (error) {
      dispatch({ type: 'COMMAND_REJECTED', commandId: command?.commandId, code: error?.code || 'COMMAND_FAILED' })
      world?.ui?.notify?.({ kind: 'error', title: 'BUILD MODE НЕДОСТУПЕН', message: 'Команда не была подтверждена сервером.', dedupeKey: 'build:command-failed' })
    } finally {
      commandInFlightRef.current = false
    }
  }, [activeItem, adapter, busy, canEdit, canPlace, editing, solution, state.catalog, state.plotId, state.plotVersion, state.revision, world])

  const remove = useCallback(async () => {
    if (!canDelete || !editing || busy || commandInFlightRef.current || !state.plotId || !state.plotVersion || !world?.ui?.confirm) return
    const confirmed = await world.ui.confirm({ kind: 'destructive', tone: 'warning', safeEnter: false, title: 'УДАЛИТЬ ОБЪЕКТ?', message: 'Объект будет удалён только после authoritative storage commit.', dedupeKey: `build-delete:${editing.id}:${editing.version}` })
    if (!confirmed) return
    const command = createPlacementDeleteCommand({ baseRevision: state.revision, plotId: state.plotId, plotVersion: state.plotVersion, targetPlacementId: editing.id, objectVersion: editing.version })
    commandInFlightRef.current = true
    dispatch({ type: 'MUTATION_STARTED' })
    try {
      const result = await adapter.submit(command)
      if (result.status === 'accepted') {
        dispatch({ type: 'COMMAND_ACCEPTED', commandId: command.commandId, snapshot: result.snapshot })
        world.ui.notify?.({ kind: 'success', title: 'ОБЪЕКТ УДАЛЁН', message: 'Сервер подтвердил новое состояние участка.', dedupeKey: `build:${command.commandId}` })
      } else {
        dispatch({ type: 'COMMAND_REJECTED', commandId: command.commandId, code: result.code, snapshot: result.snapshot })
        world.ui.notify?.({ kind: 'error', title: 'УДАЛЕНИЕ ОТКЛОНЕНО', message: COMMAND_ERROR_LABELS[result.code] || result.code, dedupeKey: `build-error:${command.commandId}` })
      }
    } catch (error) {
      dispatch({ type: 'COMMAND_REJECTED', commandId: command.commandId, code: error?.code || 'COMMAND_FAILED' })
    } finally {
      commandInFlightRef.current = false
    }
  }, [adapter, busy, canDelete, editing, state.plotId, state.plotVersion, state.revision, world])

  useEffect(() => {
    if (!open || !canBuild) return
    const onKeyDown = event => {
      if (isInteractiveTarget(event.target)) {
        if (event.code === 'Enter' && event.target?.closest?.('button')) {
          event.preventDefault()
          event.stopImmediatePropagation()
          event.target.closest('button').click()
        }
        return
      }
      const actions = {
        ArrowUp: () => move(0, -1),
        ArrowDown: () => move(0, 1),
        ArrowLeft: () => move(-1, 0),
        ArrowRight: () => move(1, 0),
        KeyR: rotate,
        Enter: commit,
        Delete: remove,
        Escape: () => (activeItem ? dispatch({ type: 'CANCEL_SELECTION' }) : onClose()),
      }
      if (actions[event.code]) {
        event.preventDefault()
        event.stopImmediatePropagation()
        if (event.repeat && !event.code.startsWith('Arrow')) return
        actions[event.code]()
      }
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [activeItem, canBuild, commit, move, onClose, open, remove, rotate])

  if (!open || !canBuild) return null
  const selectCategory = category => {
    const first = category === 'ready' ? state.readyBlueprints[0] : state.catalog?.modules.find(module => module.category === category)
    dispatch({ type: 'SELECT_CATEGORY', category, selectedId: first?.id || null })
  }

  return (
    <section
      className='testcs-build-mode'
      aria-label='TESTCS Build Mode'
      css={css`
        --build-bg: ${COLORS.bgPanel};
        --build-line: ${COLORS.border};
        --build-green: ${COLORS.success};
        --build-red: ${COLORS.error};
        --build-radius: ${RADII.xxl};
        position: absolute;
        inset: 0;
        z-index: 24;
        pointer-events: none;

        button { font: inherit; }
        .build-panel, .build-plot, .build-status { pointer-events: auto; backdrop-filter: blur(22px) saturate(125%); }
        .build-status {
          position: absolute; top: calc(5.6rem + env(safe-area-inset-top)); left: 1.25rem;
          display: flex; align-items: center; gap: .6rem; padding: .48rem .65rem;
          border: 1px solid rgba(255, 193, 92, .28); border-radius: .65rem;
          background: rgba(14, 12, 8, .82); color: #ffc15c; font-size: .58rem; letter-spacing: .1em;
        }
        .build-plot {
          position: absolute; left: 1.25rem; bottom: 6.6rem; width: min(31rem, calc(100vw - 26rem)); height: min(28rem, calc(100vh - 13rem));
          min-width: 22rem; min-height: 18rem; overflow: hidden; border: 1px solid var(--build-line); border-radius: var(--build-radius);
          background: var(--build-bg);
        }
        .build-plot-head { height: 3rem; padding: 0 .85rem; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--build-line); }
        .build-plot-title { display: grid; gap: .1rem; }
        .build-plot-title strong { font-size: .7rem; letter-spacing: .14em; }
        .build-plot-title span { color: rgba(255,255,255,.42); font-size: .5rem; letter-spacing: .1em; }
        .build-revision { color: var(--build-green); font-size: .52rem; letter-spacing: .12em; }
        .build-grid {
          position: absolute; inset: 3rem 0 5.1rem; margin: 1rem; border: 1px solid rgba(157,255,99,.2);
          background-color: #0b1112;
          background-image: linear-gradient(rgba(157,255,99,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(157,255,99,.07) 1px, transparent 1px);
          background-size: 8.333% 8.333%;
        }
        .build-grid::before { content: '24 × 24 M DEV PLOT'; position: absolute; top: .45rem; left: .55rem; color: rgba(255,255,255,.25); font-size: .48rem; letter-spacing: .12em; }
        .plot-item { position: absolute; min-width: 3px; min-height: 3px; padding:0; border: 1px solid rgba(255,255,255,.32); background: rgba(132,155,158,.36); display: grid; place-items: center; overflow: hidden; color: rgba(255,255,255,.72); font-size: .43rem; pointer-events:auto; }
        button.plot-item { cursor:pointer; }
        .plot-item.selected { border:2px solid var(--build-green); background:rgba(157,255,99,.2); z-index:2; }
        .plot-empty { position:absolute; inset:0; display:grid; place-items:center; color:${COLORS.textSecondary}; font-size:.55rem; letter-spacing:.1em; text-align:center; pointer-events:none; }
        .plot-item.pending { border-style: dashed; }
        .plot-item.ghost { z-index: 3; border: 2px solid var(--build-green); background: rgba(157,255,99,.24); box-shadow: 0 0 1.2rem rgba(157,255,99,.18); }
        .plot-item.ghost.invalid { border-color: var(--build-red); background: rgba(255,102,95,.24); box-shadow: 0 0 1.2rem rgba(255,102,95,.16); }
        .build-ghost-state { position: absolute; left: 1rem; right: 1rem; bottom: 1rem; height: 3.1rem; padding: 0 .8rem; display: flex; align-items: center; gap: .65rem; border: 1px solid rgba(157,255,99,.28); background: rgba(157,255,99,.08); border-radius: .7rem; }
        .build-ghost-state.invalid { border-color: rgba(255,102,95,.35); background: rgba(255,102,95,.09); }
        .build-ghost-state svg { width: 1rem; color: var(--build-green); }
        .build-ghost-state.invalid svg { color: var(--build-red); }
        .build-ghost-copy { flex: 1; display: grid; gap: .14rem; }
        .build-ghost-copy strong { font-size: .62rem; letter-spacing: .1em; }
        .build-ghost-copy span { color: rgba(255,255,255,.46); font-size: .5rem; }
        .build-panel { position: absolute; top: calc(5.6rem + env(safe-area-inset-top)); right: calc(1.25rem + env(safe-area-inset-right)); bottom: calc(6.6rem + env(safe-area-inset-bottom)); width: 22rem; display: flex; flex-direction: column; border: 1px solid var(--build-line); border-radius: var(--build-radius); background: var(--build-bg); overflow: hidden; }
        .build-panel-head { min-height: 4rem; padding: .75rem .85rem; display: flex; align-items: center; gap: .65rem; border-bottom: 1px solid var(--build-line); }
        .build-logo { width: 2.15rem; height: 2.15rem; display: grid; place-items: center; border: 1px solid rgba(157,255,99,.4); color: var(--build-green); background: rgba(157,255,99,.08); border-radius: .65rem; }
        .build-logo svg { width: 1.1rem; }
        .build-heading { flex: 1; display: grid; gap: .12rem; }
        .build-heading strong { font-size: .75rem; letter-spacing: .15em; }
        .build-heading span { color: rgba(255,255,255,.4); font-size: .5rem; letter-spacing: .1em; }
        .build-close { width: 2rem; height: 2rem; border: 1px solid var(--build-line); border-radius: .55rem; color: rgba(255,255,255,.7); background: transparent; display: grid; place-items: center; cursor: pointer; }
        .build-close svg { width: .95rem; }
        .build-categories { display: flex; gap: .35rem; padding: .65rem; overflow-x: auto; border-bottom: 1px solid var(--build-line); scrollbar-width: none; }
        .build-category { flex: 0 0 auto; height: 1.9rem; padding: 0 .65rem; border: 1px solid var(--build-line); border-radius: .55rem; background: transparent; color: rgba(255,255,255,.5); font-size: .49rem; letter-spacing: .09em; cursor: pointer; }
        .build-category.active { border-color: rgba(157,255,99,.4); color: var(--build-green); background: rgba(157,255,99,.09); }
        .build-items { flex: 1; min-height: 0; padding: .65rem; overflow-y: auto; display: grid; grid-template-columns: 1fr 1fr; align-content: start; gap: .5rem; }
        .build-item { min-height: 8rem; padding: .65rem; text-align: left; border: 1px solid var(--build-line); border-radius: .72rem; background: rgba(255,255,255,.025); color: white; cursor: pointer; display: flex; flex-direction: column; gap: .45rem; }
        .build-item:hover { border-color: rgba(157,255,99,.3); }
        .build-item.selected { border-color: rgba(157,255,99,.55); background: rgba(157,255,99,.08); }
        .build-item-art { height: 3rem; display: grid; place-items: center; border: 1px solid rgba(255,255,255,.08); border-radius: .5rem; background: linear-gradient(145deg, rgba(157,255,99,.1), rgba(255,255,255,.015)); color: rgba(157,255,99,.8); }
        .build-item-art svg { width: 1.45rem; }
        .build-item-name { min-height: 2em; font-size: .58rem; line-height: 1.25; font-weight: 650; }
        .build-item-meta { margin-top: auto; display: flex; justify-content: space-between; color: rgba(255,255,255,.42); font-size: .48rem; }
        .build-selected { padding: .75rem; display: grid; gap: .58rem; border-top: 1px solid var(--build-line); }
        .build-selected-main { display: flex; align-items: center; gap: .6rem; }
        .build-selected-main strong { font-size: .65rem; }
        .build-selected-main span { display: block; margin-top: .15rem; color: rgba(255,255,255,.42); font-size: .49rem; }
        .build-controls { display: grid; grid-template-columns: 2.2rem 2.2rem 2.2rem 1fr; gap: .35rem; }
        .build-control, .build-place, .build-undo { min-height: 2.25rem; border: 1px solid var(--build-line); border-radius: .58rem; color: rgba(255,255,255,.7); background: rgba(255,255,255,.03); display: flex; align-items: center; justify-content: center; gap: .35rem; cursor: pointer; }
        .build-control svg, .build-undo svg { width: .9rem; }
        .build-place { border-color: rgba(157,255,99,.46); background: var(--build-green); color: #081008; font-size: .55rem; font-weight: 750; letter-spacing: .1em; }
        .build-place:disabled { border-color: var(--build-line); background: rgba(255,255,255,.08); color: rgba(255,255,255,.24); cursor: not-allowed; }
        .build-undo { width: 2.25rem; }
        .build-footer-row { display: flex; gap: .4rem; }
        .build-footer-row .build-place { flex: 1; }
        .build-empty { grid-column: 1 / -1; min-height: 8rem; display: grid; place-items: center; color: rgba(255,255,255,.38); font-size: .58rem; text-align: center; }
        .build-command-error { margin: 0 .65rem .15rem; padding: .55rem .65rem; display: flex; align-items: center; gap: .55rem; border: 1px solid rgba(255,102,95,.42); border-radius: .58rem; background: rgba(255,102,95,.1); color: #ff9a94; font-size: .52rem; letter-spacing: .07em; }
        .build-command-error span { flex: 1; }
        .build-command-error button { width: 1.7rem; height: 1.7rem; display: grid; place-items: center; border: 0; border-radius: .4rem; background: rgba(255,255,255,.06); color: inherit; cursor: pointer; }
        .build-command-error svg { width: .8rem; }
        .build-load { flex: 1; display: grid; place-items: center; padding: 2rem; text-align: center; color: rgba(255,255,255,.55); font-size: .62rem; line-height: 1.5; }
        .build-load svg { width: 1.4rem; margin: 0 auto .65rem; color: var(--build-green); }
        .build-load .spin { animation: build-spin 1s linear infinite; }
        @keyframes build-spin { to { transform: rotate(360deg); } }
        @media all and (max-width: 760px) {
          .build-status { top: calc(4.35rem + env(safe-area-inset-top)); left: .7rem; right: .7rem; justify-content: center; }
          .build-plot { top: calc(7rem + env(safe-area-inset-top)); left: .7rem; right: .7rem; bottom: auto; width: auto; min-width: 0; height: 25vh; min-height: 10rem; }
          .build-grid { inset: 3rem 0 0; margin: .65rem; }
          .build-ghost-state { display: none; }
          .build-panel { top: auto; left: 0; right: 0; bottom: 0; width: auto; height: min(58vh, 35rem); border-radius: 1rem 1rem 0 0; padding-bottom: env(safe-area-inset-bottom); }
          .build-panel-head { min-height: 3.25rem; padding: .5rem .7rem; }
          .build-logo { width: 1.9rem; height: 1.9rem; }
          .build-categories { padding: .5rem; }
          .build-items { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .build-item { min-height: 6.8rem; }
          .build-selected { padding: .55rem .7rem; }
        }
        @media all and (max-width: 760px) and (max-height: 620px) {
          .build-status { top: calc(3.8rem + env(safe-area-inset-top)); }
          .build-plot { top: calc(5.7rem + env(safe-area-inset-top)); height: 22vh; min-height: 7rem; }
          .build-panel { height: min(55vh, 26rem); }
          .build-item { min-height: 5.8rem; }
        }
        @media all and (min-width: 761px) and (max-height: 560px) {
          .build-status { top: calc(.55rem + env(safe-area-inset-top)); left: .7rem; }
          .build-plot { top: calc(3.3rem + env(safe-area-inset-top)); left: .7rem; bottom: calc(.7rem + env(safe-area-inset-bottom)); width: calc(45vw - 1.05rem); height: auto; min-width: 0; min-height: 0; }
          .build-panel { top: calc(.55rem + env(safe-area-inset-top)); right: calc(.55rem + env(safe-area-inset-right)); bottom: calc(.55rem + env(safe-area-inset-bottom)); width: min(52vw, 32rem); }
          .build-panel-head { min-height: 3.1rem; padding: .45rem .65rem; }
          .build-logo { width: 1.85rem; height: 1.85rem; }
          .build-categories { padding: .42rem; }
          .build-items { grid-template-columns: repeat(3, minmax(0, 1fr)); padding: .48rem; }
          .build-item { min-height: 5.5rem; padding: .45rem; }
          .build-item-art { height: 2rem; }
          .build-selected { padding: .45rem .6rem; }
        }
      `}
    >
      <div className='build-status'>
        {!state.capabilities.length
          ? 'НЕТ ПРАВ УЧАСТКА · BUILD MODE ТОЛЬКО ДЛЯ ВЛАДЕЛЬЦА ИЛИ ЯВНОГО ГРАНТА'
          : adapter.persistent
          ? authorityOnline
            ? 'SERVER SYNC · ДОМ ВИДЕН В МИРЕ И СОХРАНЯЕТСЯ'
            : 'SERVER OFFLINE · УСТАНОВКА ЗАБЛОКИРОВАНА'
          : 'LOCAL PREVIEW · НЕ СОХРАНЯЕТСЯ НА СЕРВЕРЕ'}
      </div>

      <div className='build-plot'>
        <div className='build-plot-head'>
          <div className='build-plot-title'>
            <strong>УЧАСТОК / GRID 0.5 M</strong>
            <span>X {solution?.transform.positionM[0] ?? 0} · Z {solution?.transform.positionM[2] ?? 0} · R {solution?.transform.rotationDeg[1] ?? 0}°</span>
          </div>
          <div className='build-revision'>REV {state.revision}</div>
        </div>
        <div className='build-grid'>
          {!state.placements.length && !state.pending.length && !activeItem && <span className='plot-empty'>НА УЧАСТКЕ НЕТ BUILD-ОБЪЕКТОВ</span>}
          {state.placements.map(placement => {
            const item = itemForPlacement(placement, state.catalog)
            return item ? <button type='button' disabled={!canEdit && !canDelete} aria-label={`Выбрать ${item.displayName}`} key={placement.id} className={`plot-item ${editing?.id === placement.id ? 'selected' : ''}`} style={plotStyle(placementAabb(item, placement.transform))} onClick={() => dispatch({ type: 'SELECT_PLACEMENT', placement })}>{editing?.id === placement.id ? <PencilIcon /> : null}</button> : null
          })}
          {state.pending.map(placement => (
            <div key={placement.id} className='plot-item pending' style={plotStyle(placementAabb(placement.preview, placement.transform))} />
          ))}
          {activeItem && solution && (
            <div className={`plot-item ghost ${solution.valid ? '' : 'invalid'}`} style={plotStyle(solution.aabb)}>
              {editing ? 'EDIT' : activeItem.kind === 'blueprint' ? 'HOUSE' : activeItem.category?.toUpperCase()}
            </div>
          )}
        </div>
        {activeItem && solution && (
          <div className={`build-ghost-state ${solution.valid ? '' : 'invalid'}`}>
            {solution.valid ? <CheckIcon /> : <AlertTriangleIcon />}
            <div className='build-ghost-copy'>
              <strong>{solution.valid ? 'МОЖНО УСТАНОВИТЬ' : solution.reasons.map(reason => REASON_LABELS[reason]).join(' · ')}</strong>
              <span>{solution.snap ? `SNAP → ${solution.snap.targetPointId}` : 'GRID SNAP · ПРЕДПРОСМОТР'}</span>
            </div>
          </div>
        )}
      </div>

      <aside className='build-panel'>
        <header className='build-panel-head'>
          <div className='build-logo'><BoxIcon /></div>
          <div className='build-heading'><strong>TESTCS BUILD MODE</strong><span>PHASE 1 / MODULAR</span></div>
          <button type='button' className='build-close' onClick={onClose} aria-label='Закрыть Build Mode'><XIcon /></button>
        </header>

        {state.status === 'loading' && <div className='build-load'><div><LoaderCircleIcon className='spin' />ЗАГРУЖАЕМ КАТАЛОГ</div></div>}
        {state.status === 'error' && <div className='build-load'><div><AlertTriangleIcon />КАТАЛОГ НЕ ЗАГРУЖЕН<br />{state.error}</div></div>}
        {state.status === 'ready' && (
          <>
            <nav className='build-categories' aria-label='Категории строительства'>
              {categories.map(category => (
                <button key={category} type='button' className={`build-category ${category === state.category ? 'active' : ''}`} onClick={() => selectCategory(category)}>
                  {CATEGORY_LABELS[category] || category.toUpperCase()}
                </button>
              ))}
            </nav>
            <div className='build-items'>
              {items.length === 0 && <div className='build-empty'>В ЭТОЙ КАТЕГОРИИ ПОКА ПУСТО</div>}
              {items.map(item => (
                <button key={item.id} type='button' className={`build-item ${selected?.id === item.id ? 'selected' : ''}`} onClick={() => dispatch({ type: 'SELECT_ITEM', id: item.id })}>
                  <div className='build-item-art'>{item.kind === 'blueprint' ? <HouseIcon /> : <BoxIcon />}</div>
                  <div className='build-item-name'>{item.displayName}</div>
                  <div className='build-item-meta'><span>{item.kind === 'blueprint' ? `${item.elementCount} ЭЛ.` : item.dimensionsM.join('×')}</span><span>{item.cost?.credits ?? 0} BC</span></div>
                </button>
              ))}
            </div>
            {state.error && (
              <div className='build-command-error' role='alert'>
                <span>{COMMAND_ERROR_LABELS[state.error] || state.error}</span>
                <button type='button' onClick={() => dispatch({ type: 'CLEAR_ERROR' })} aria-label='Скрыть ошибку'><XIcon /></button>
              </div>
            )}
            <footer className='build-selected'>
              <div className='build-selected-main'>
                <div><strong>{activeItem?.displayName || 'ВЫБЕРИТЕ МОДУЛЬ'}</strong><span>{activeItem ? `${editing ? 'РЕДАКТИРОВАНИЕ · ' : ''}${activeItem.dimensionsM.join(' × ')} M` : 'Каталог готов'}</span></div>
              </div>
              <div className='build-controls' aria-label='Перемещение модуля'>
                <button type='button' className='build-control' onClick={() => move(-1, 0)} aria-label='Влево'><ArrowLeftIcon /></button>
                <button type='button' className='build-control' onClick={() => move(0, -1)} aria-label='Вперёд'><ArrowUpIcon /></button>
                <button type='button' className='build-control' onClick={() => move(1, 0)} aria-label='Вправо'><ArrowRightIcon /></button>
                <button type='button' className='build-control' onClick={rotate}><RotateCwIcon /> R</button>
                <button type='button' className='build-control' onClick={() => move(0, 1)} aria-label='Назад'><ArrowDownIcon /></button>
                <button type='button' className='build-control' onClick={() => dispatch({ type: 'CANCEL_SELECTION' })} aria-label='Отменить выбор'><XIcon /></button>
              </div>
              <div className='build-footer-row'>
                {editing && <button type='button' className='build-undo' onClick={remove} disabled={!canDelete || busy} aria-label='Удалить выбранный объект'><Trash2Icon /></button>}
                <button type='button' className='build-place' onClick={commit} disabled={!(editing ? canEdit : canPlace) || !activeItem || !solution?.valid || busy}>{busy ? 'СИНХРОНИЗАЦИЯ…' : editing ? 'ПРОВЕРИТЬ И СОХРАНИТЬ · ENTER' : 'ПРОВЕРИТЬ И УСТАНОВИТЬ · ENTER'}</button>
              </div>
            </footer>
          </>
        )}
      </aside>
    </section>
  )
}
